import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  runTransaction,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import "./AdminCopyTradingRequest.css";

const money = (value) =>
  Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 8,
  });

const getStatus = (status) => String(status || "pending").toLowerCase();

function getRiskLimit(trade) {
  const value = trade?.riskLimit;
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 && number <= 100
    ? number
    : null;
}

export default function AdminCopyTradingRequest() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const requestsQuery = query(
        collection(db, "copyTrades"),
        orderBy("createdAt", "desc")
      );
      const snapshot = await getDocs(requestsQuery);
      setRequests(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    } catch (err) {
      console.error("Failed to load copy trading requests:", err);
      setError("Imeshindikana kupakia maombi. Kagua muunganisho na Firestore rules.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const filteredRequests = useMemo(() => {
    const term = search.trim().toLowerCase();
    return requests.filter((item) => {
      const status = getStatus(item.status);
      const matchesStatus = statusFilter === "all" || status === statusFilter;
      const haystack = [
        item.username,
        item.traderName,
        item.traderUsername,
        item.pair,
        item.id,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return matchesStatus && (!term || haystack.includes(term));
    });
  }, [requests, search, statusFilter]);

  const summary = useMemo(() => {
    const pending = requests.filter((item) => getStatus(item.status) === "pending").length;
    const won = requests.filter((item) => getStatus(item.status) === "won").length;
    const lost = requests.filter((item) => getStatus(item.status) === "lost").length;
    return { total: requests.length, pending, won, lost };
  }, [requests]);

  const changeStatus = async (item, newStatus) => {
    if (getStatus(item.status) !== "pending") {
      setError("Ombi hili limeshashughulikiwa. Payout haiwezi kurudiwa.");
      setMessage("");
      return;
    }

    const investment = Number(item.amount);
    const riskLimit = getRiskLimit(item);

    if (newStatus === "won") {
      if (!Number.isFinite(investment) || investment <= 0) {
        setError("Kiasi cha uwekezaji kwenye ombi si sahihi.");
        return;
      }
      if (riskLimit === null) {
        setError("Alert asilimia haipo au si sahihi. Hakikisha riskLimit imehifadhiwa kwenye copyTrades.");
        return;
      }
    }

    const profitPreview = Number((investment * (riskLimit || 0) / 100).toFixed(8));
    const payoutPreview = Number((investment + profitPreview).toFixed(8));
    const confirmation = newStatus === "won"
      ? `WIN — ${item.username || "mtumiaji"}\n\nMtaji: ${money(investment)} USDT\nAlert aliyochagua: ${riskLimit}%\nFaida: ${money(profitPreview)} USDT\nPayout yote: ${money(payoutPreview)} USDT\n\nEndelea?`
      : `Una uhakika kuweka ombi hili kuwa LOST? Hakuna payout itakayoongezwa.`;

    if (!window.confirm(confirmation)) return;

    setBusyId(item.id);
    setError("");
    setMessage("");

    try {
      let userRef = null;
      if (newStatus === "won") {
        const username = String(item.username || "").trim();
        if (!username) throw new Error("USERNAME_MISSING");
        const userQuery = query(
          collection(db, "users"),
          where("username", "==", username)
        );
        const userSnapshot = await getDocs(userQuery);
        if (userSnapshot.size !== 1) throw new Error("USER_NOT_FOUND_OR_DUPLICATED");
        userRef = userSnapshot.docs[0].ref;
      }

      const result = await runTransaction(db, async (transaction) => {
        const tradeRef = doc(db, "copyTrades", item.id);
        const tradeSnapshot = await transaction.get(tradeRef);
        if (!tradeSnapshot.exists()) throw new Error("REQUEST_NOT_FOUND");

        const trade = tradeSnapshot.data();
        if (getStatus(trade.status) !== "pending") throw new Error("ALREADY_PROCESSED");
        const updatedAt = new Date().toISOString();

        if (newStatus === "lost") {
          transaction.update(tradeRef, { status: "lost", updatedAt });
          return { status: "lost", updatedAt };
        }

        // Read the alert from the saved Firestore order, not from a fixed percentage.
        const actualInvestment = Number(trade.amount);
        const actualRiskLimit = getRiskLimit(trade);
        if (!Number.isFinite(actualInvestment) || actualInvestment <= 0 || actualRiskLimit === null) {
          throw new Error("INVALID_TRADE_DATA");
        }

        const userSnapshot = await transaction.get(userRef);
        if (!userSnapshot.exists()) throw new Error("USER_NOT_FOUND");
        const currentBalance = Number(userSnapshot.data().usdtBalanceOther ?? 0);
        if (!Number.isFinite(currentBalance) || currentBalance < 0) throw new Error("INVALID_BALANCE");

        const profitAmount = Number((actualInvestment * actualRiskLimit / 100).toFixed(8));
        const payoutAmount = Number((actualInvestment + profitAmount).toFixed(8));
        const newBalance = Number((currentBalance + payoutAmount).toFixed(8));

        transaction.update(userRef, { usdtBalanceOther: newBalance });
        transaction.update(tradeRef, {
          status: "won",
          profitPercent: actualRiskLimit,
          profitAmount,
          payoutAmount,
          updatedAt,
        });

        return {
          status: "won",
          profitPercent: actualRiskLimit,
          profitAmount,
          payoutAmount,
          updatedAt,
        };
      });

      setRequests((current) => current.map((row) =>
        row.id === item.id
          ? {
              ...row,
              status: result.status,
              updatedAt: result.updatedAt,
              ...(result.status === "won"
                ? {
                    profitPercent: result.profitPercent,
                    profitAmount: result.profitAmount,
                    payoutAmount: result.payoutAmount,
                  }
                : {}),
            }
          : row
      ));

      setMessage(result.status === "won"
        ? `WIN imefanikiwa. Faida ${money(result.profitAmount)} USDT; payout yote ${money(result.payoutAmount)} USDT imeongezwa kwenye salio.`
        : "Ombi limewekwa LOST. Hakuna payout iliyoongezwa.");
    } catch (err) {
      console.error("Copy trading status/payout error:", err);
      const errors = {
        USERNAME_MISSING: "Username ya ombi haipo.",
        USER_NOT_FOUND_OR_DUPLICATED: "Mtumiaji hajapatikana au username inajirudia kwenye users.",
        REQUEST_NOT_FOUND: "Ombi halijapatikana kwenye Firestore.",
        ALREADY_PROCESSED: "Ombi limeshashughulikiwa; payout haitarudiwa.",
        USER_NOT_FOUND: "Akaunti ya mtumiaji haijapatikana.",
        INVALID_BALANCE: "Salio la mtumiaji si sahihi.",
        INVALID_TRADE_DATA: "Kiasi au riskLimit iliyohifadhiwa kwenye ombi si sahihi.",
      };
      setError(errors[err.message] || "Imeshindikana kusasisha ombi. Kagua Firestore rules na ruhusa za admin.");
    } finally {
      setBusyId("");
    }
  };

  const deleteRequest = async (item) => {
    if (!window.confirm(`Una uhakika unataka kufuta ombi ${item.id}?`)) return;
    setBusyId(item.id);
    setError("");
    setMessage("");
    try {
      await deleteDoc(doc(db, "copyTrades", item.id));
      setRequests((current) => current.filter((row) => row.id !== item.id));
      setMessage("Ombi limefutwa.");
    } catch (err) {
      console.error("Failed to delete copy trading request:", err);
      setError("Imeshindikana kufuta ombi. Kagua Firestore rules.");
    } finally {
      setBusyId("");
    }
  };

  const formatDate = (value) => {
    if (!value) return "—";
    const date = typeof value?.toDate === "function" ? value.toDate() : new Date(value);
    return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
  };

  return (
    <div className="admin-copy-trading-page">
      <div className="admin-page-header">
        <div>
          <h1>Copy Trading Requests</h1>
          <p>Simamia maombi na payout za copy trading.</p>
        </div>
        <button type="button" onClick={fetchRequests} disabled={loading || !!busyId}>
          {loading ? "Inapakia..." : "Refresh"}
        </button>
      </div>

      <div className="copy-trading-summary">
        <div className="summary-card"><span>Jumla</span><strong>{summary.total}</strong></div>
        <div className="summary-card"><span>Pending</span><strong>{summary.pending}</strong></div>
        <div className="summary-card"><span>Won</span><strong>{summary.won}</strong></div>
        <div className="summary-card"><span>Lost</span><strong>{summary.lost}</strong></div>
      </div>

      {message && <div className="admin-success-message" role="status">{message}</div>}
      {error && <div className="admin-error-message" role="alert">{error}</div>}

      <div className="admin-copy-trading-controls">
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Tafuta username, trader, pair au ID..."
          aria-label="Tafuta maombi"
        />
        <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} aria-label="Chuja kwa status">
          <option value="all">Status zote</option>
          <option value="pending">Pending</option>
          <option value="won">Won</option>
          <option value="lost">Lost</option>
        </select>
      </div>

      <div className="admin-table-wrapper">
        <table className="admin-copy-trading-table">
          <thead>
            <tr>
              <th>Mtumiaji</th>
              <th>Trader</th>
              <th>Pair</th>
              <th>Mtaji (USDT)</th>
              <th>Risk level</th>
              <th>Alert %</th>
              <th>Faida (USDT)</th>
              <th>Payout (USDT)</th>
              <th>Status</th>
              <th>Tarehe</th>
              <th>Request ID</th>
              <th>Vitendo</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="12" className="admin-table-empty">Inapakia maombi...</td></tr>
            ) : filteredRequests.length === 0 ? (
              <tr><td colSpan="12" className="admin-table-empty">Hakuna maombi yaliyopatikana.</td></tr>
            ) : filteredRequests.map((item) => {
              const status = getStatus(item.status);
              const riskLimit = getRiskLimit(item);
              const isBusy = busyId === item.id;
              return (
                <tr key={item.id}>
                  <td>{item.username || "—"}</td>
                  <td>{item.traderName || item.traderUsername || "—"}</td>
                  <td>{item.pair || "—"}</td>
                  <td>{money(item.amount)}</td>
                  <td>{item.riskLevel || "—"}</td>
                  <td>{riskLimit === null ? "—" : `${riskLimit}%`}</td>
                  <td>{item.profitAmount == null ? "—" : money(item.profitAmount)}</td>
                  <td>{item.payoutAmount == null ? "—" : money(item.payoutAmount)}</td>
                  <td><span className={`status-badge status-${status}`}>{status}</span></td>
                  <td>{formatDate(item.createdAt)}</td>
                  <td className="request-id-cell" title={item.id}>{item.id}</td>
                  <td>
                    <div className="admin-row-actions">
                      <button type="button" className="win-button" disabled={isBusy || status !== "pending" || riskLimit === null} onClick={() => changeStatus(item, "won")}>
                        {isBusy ? "..." : "WIN"}
                      </button>
                      <button type="button" className="lost-button" disabled={isBusy || status !== "pending"} onClick={() => changeStatus(item, "lost")}>
                        LOST
                      </button>
                      <button type="button" className="delete-button" disabled={isBusy} onClick={() => deleteRequest(item)}>
                        DELETE
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="admin-copy-trading-note">
        WIN huongeza mtaji pamoja na faida iliyohesabiwa kwa kutumia riskLimit iliyohifadhiwa kwenye ombi. Hakikisha Firestore rules zinaruhusu mabadiliko haya kwa admin pekee.
      </p>
    </div>
  );
}
